import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { getConsentForm, submitConsent } from '../services/consentApi';
import { FormRenderResponse, ConsentFormData, ApiError } from '../types/consent';
import { SimpleConsentWizard } from '../components/ConsentWizard';


const ConsentCollection: React.FC = () => {
  const { tenantId, formId, urlId: paramUrlId } = useParams<{ tenantId?: string; formId?: string; urlId?: string }>();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<FormRenderResponse | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [redirectTime, setRedirectTime] = useState<number>(5);

  const urlId = formId || paramUrlId || '';
  const token = searchParams.get('token') || '';

  useEffect(() => {
    const loadForm = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await getConsentForm(tenantId || '', urlId, token);
        setFormData(data);
      } catch (err: any) {
        const apiError = err.response?.data as ApiError;
        setFormData(null);
        setError(apiError?.userMessage || 'Error loading consent form');
      } finally {
        setLoading(false);
      }
    };

    loadForm();
  }, [tenantId, urlId, token]);

  const handleComplete = async (data: ConsentFormData) => {
    try {
      const response = await submitConsent(tenantId || '', urlId, token, data);
      setSuccess(true);
      
      // Handle redirect URL from response or formData
      const successUrl = response.redirectUrl || formData?.successRedirectUrl;
      const successTime = formData?.successRedirectTime || 5;
      
      if (successUrl) {
        // Ensure URL has protocol, default to https if missing
        const normalizedUrl = normalizeUrl(successUrl);
        setRedirectUrl(normalizedUrl);
        setRedirectTime(successTime);
        
        // Auto-redirect after specified time
        setTimeout(() => {
          window.location.href = normalizedUrl;
        }, successTime * 1000);
      }
    } catch (err: any) {
      const apiError = err.response?.data as ApiError;
      
      // Handle failure redirect
      const failureUrl = formData?.failureRedirectUrl;
      const failureTime = formData?.failureRedirectTime || 5;
      
      if (failureUrl) {
        // Ensure URL has protocol, default to https if missing
        const normalizedUrl = normalizeUrl(failureUrl);
        setRedirectUrl(normalizedUrl);
        setRedirectTime(failureTime);
        setError(apiError?.userMessage || 'Failed to submit consent. Redirecting...');
        
        setTimeout(() => {
          window.location.href = normalizedUrl;
        }, failureTime * 1000);
      } else {
        throw new Error(apiError?.userMessage || 'Failed to submit consent');
      }
    }
  };

  // Helper function to normalize URL - adds https:// if no protocol present
  const normalizeUrl = (url: string): string => {
    if (!url) return url;
    const trimmedUrl = url.trim();
    // Check if URL already has a protocol
    if (trimmedUrl.match(/^https?:\/\//i)) {
      return trimmedUrl;
    }
    // Add https:// if no protocol
    return `https://${trimmedUrl}`;
  };

  if (loading) {
    return (
      <div className="container consent-shell">
        <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
          <div style={{ marginBottom: '16px' }}><Loader2 size={32} className="spin" /></div>
          <p>Loading consent form...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px',
        background: 'white'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '320px' }}>
          <h1 style={{
            color: '#11936f',
            fontSize: '20px',
            fontWeight: 600,
            marginBottom: '12px',
            fontFamily: 'Roboto, sans-serif'
          }}>
            Consent Recorded Successfully
          </h1>
          <p style={{
            color: '#4A4E6A',
            fontSize: '14px',
            lineHeight: 1.5,
            fontFamily: 'Roboto, sans-serif'
          }}>
            Your Consent has been securely recorded and will be processed according to the terms you agreed to.
          </p>
          {redirectUrl && (
            <p style={{ color: '#6E6E6E', marginTop: '24px', fontSize: '13px' }}>
              Redirecting in {redirectTime} seconds...
              <br />
              <a href={redirectUrl} style={{ color: '#EA582C' }}>
                Click here if not redirected automatically
              </a>
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
          <div style={{ marginBottom: '16px' }}><AlertTriangle size={40} color="#EA582C" /></div>
          <h2 style={{ color: 'var(--ds-danger)', marginBottom: '16px' }}>Error</h2>
          <p style={{ color: 'var(--ds-text-secondary)' }}>{error || 'Error loading consent form'}</p>
        </div>
      </div>
    );
  }

  return (
    <SimpleConsentWizard
      formData={formData}
      onComplete={handleComplete}
      tenantId={tenantId || ''}
      urlId={urlId}
      token={token}
    />
  );
};

export default ConsentCollection;
